import { useMemo, useState } from "react";
import { Link, useLoaderData, useSearchParams } from "react-router-dom";

import ServerFooter from "../common/ServerFooter";
import UserInfo from "../common/userInfo";
import type { ApprovalRecord, ApprovalsResponse } from "./types";
import DataFilter from "../common/frame/dataFilter";
import Export, { type ApprovalExportRecord } from "./export";

const dataFrame = [
    { label: "Project", key: "projectName" },
    { label: "Project Category", key: "projectCategory" },
    { label: "Work Category", key: "workCategory" },
    { label: "Labor Category", key: "laborCategory" },
    { label: "Technician", key: "userName" },
    { label: "Department", key: "department" },
    { label: "Submit Time", key: "submitTime", type: "date" },
    { label: "Planned Start Time", key: "startTime", type: "date" },
    { label: "Planned End Time", key: "endTime", type: "date" },
];

function formatPlannedTime(approval: ApprovalRecord) {
    if (approval.allDay === "yes") {
        return approval.startTime.slice(0, 10) === approval.endTime.slice(0, 10)
            ? approval.startTime.slice(0, 10)
            : `${approval.startTime.slice(0, 10)} ~ ${approval.endTime.slice(0, 10)}`;
    }

    return approval.startTime.slice(0, 10) === approval.endTime.slice(0, 10)
        ? `${approval.startTime.slice(0, 10)} ${approval.startTime.slice(11, 16)} ~ ${approval.endTime.slice(11, 16)}`
        : `${approval.startTime.slice(0, 16)} ~ ${approval.endTime.slice(0, 16)}`;
}

export default function Approvals() {
    const { approvals, total } = useLoaderData() as ApprovalsResponse;
    const [searchParams, setSearchParams] = useSearchParams();
    const defaultFilter = useMemo<Record<string, string>>(() => Object.fromEntries(dataFrame.map(item => [item.key, ""])), []);
    const [curFilter, setFilter] = useState(defaultFilter);
    const setCurrentPage = (page: number) => {
        const params = new URLSearchParams(searchParams);
        params.set("page", String(page));
        setSearchParams(params, { replace: true });
    };
    const filteredApprovals = useMemo(() => approvals
        .map((approval): ApprovalExportRecord => ({
            ...approval,
            plannedTime: formatPlannedTime(approval),
        }))
        .filter(approval => {
            if (curFilter.projectName && approval.projectName !== curFilter.projectName) return false;
            if (curFilter.projectCategory && approval.projectCategory !== curFilter.projectCategory) return false;
            if (curFilter.workCategory && approval.workCategory !== curFilter.workCategory) return false;
            if (curFilter.laborCategory && approval.laborCategory !== curFilter.laborCategory) return false;
            if (curFilter.userName && approval.userName !== curFilter.userName) return false;
            if (curFilter.department && approval.department !== curFilter.department) return false;
            if (curFilter.submitTimeMin && approval.submitTime.slice(0, 10) < curFilter.submitTimeMin) return false;
            if (curFilter.submitTimeMax && approval.submitTime.slice(0, 10) > curFilter.submitTimeMax) return false;
            if (curFilter.startTimeMin && approval.startTime.slice(0, 10) < curFilter.startTimeMin) return false;
            if (curFilter.startTimeMax && approval.startTime.slice(0, 10) > curFilter.startTimeMax) return false;
            if (curFilter.endTimeMin && approval.endTime.slice(0, 10) < curFilter.endTimeMin) return false;
            if (curFilter.endTimeMax && approval.endTime.slice(0, 10) > curFilter.endTimeMax) return false;
            return true;
        }), [approvals, curFilter]);
    const filterActive = Object.entries(curFilter).some(([key, value]) => defaultFilter[key] !== value);

    return (
        <div className={"cssContent" + (total > 0 ? " pb8rem" : "")}>
            {approvals.length ? (
                <nav className="my-3 d-flex gap-3">
                    <DataFilter
                        setCurrentPage={setCurrentPage}
                        curFilter={curFilter}
                        setFilter={setFilter}
                        defaultFilter={defaultFilter}
                        items={approvals}
                        dataFrame={dataFrame}
                        width={undefined}
                        minWidth="70rem"
                        buttonClassName="btn btn-outline-primary position-relative"
                        applyOnSearch={true}
                    />
                    {filteredApprovals.length ? <Export filteredApprovals={filteredApprovals} /> : null}
                </nav>
            ) : null}
            {!filteredApprovals.length ? (
                <div className="cssLoading">Empty</div>
            ) : (
                <div className="table-responsive">
                    <table className="table">
                        <thead className="noTableTopBorder">
                            <tr>
                                <th>Project</th>
                                <th>Project Category</th>
                                <th>Work Category</th>
                                <th>Labor Category</th>
                                <th>Technician</th>
                                <th>Department</th>
                                <th>Submit Time</th>
                                <th>Planned Time</th>
                                <th/>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredApprovals.map(approval => (
                                <tr key={approval.id}>
                                    <td>
                                        <Link to={`/Projects/${approval.projectId}`}>
                                            {approval.projectName}
                                        </Link>
                                    </td>
                                    <td>{approval.projectCategory}</td>
                                    <td>{approval.workCategory}</td>
                                    <td>{approval.laborCategory}</td>
                                    <td><UserInfo id={approval.userId} name={approval.userName} /></td>
                                    <td>{approval.department}</td>
                                    <td>{approval.submitTime}</td>
                                    <td>{approval.plannedTime}</td>
                                    <td>
                                        <Link
                                            children="Detail"
                                            className="btn btn-outline-primary"
                                            to={`/Assignments/${approval.id}`}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {total > 0 ? <ServerFooter length={filterActive ? filteredApprovals.length : total} /> : null}
        </div>
    );
}
